import {
  Box,
  Stack,
  Typography,
  CircularProgress,
  Snackbar,
  Paper,
  Chip,
  LinearProgress,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { QuoteQuestion } from '@/api/endpoints/quote/useGenerateQuoteQuestions';
import { useDistanceCalculationStatus } from '@/api/endpoints/vendor/useDistanceCalculationStatus';
import { useInitiateVendorCalls } from '@/api/endpoints/vendor/useInitiateVendorCalls';
import { useSearchVendors } from '@/api/endpoints/vendor/useSearchVendors';
import { useUpdateSelectedVendors } from '@/api/endpoints/vendor/useUpdateSelectedVendors';
import {
  VendorSearchResult,
  DiyVideo,
  CostEstimate,
  PermitRequirement,
  ProjectReadinessChecklist,
  VendorQuestions,
} from '@/api/models/vendor';
import { PMPRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import {
  QuoteAnswerSessionService,
  StoredQuoteAnswers,
} from '@/services/QuoteAnswerSessionService';

import PhoneNumberModal from '@/components/dialogs/PhoneNumberModal';
import QuoteAnswersConfirmationDialog from '@/components/dialogs/QuoteAnswersConfirmationDialog';
import QuoteRequirementsDialog from '@/components/dialogs/QuoteRequirementsDialog';
import AIFeatureBanner from '@/components/vendor/AIFeatureBanner';
import VendorSearchResults from '@/components/vendor/VendorSearchResults';

interface LocationState {
  vendors: VendorSearchResult[];
  searchQuery: string;
  address: string;
  disclaimer?: string;
  search_query?: string;
  searchId?: string;
  selectedVendors?: string[];
  diy_video?: DiyVideo | null;
  cost_estimate?: CostEstimate | null;
  permit_requirement?: PermitRequirement | null;
  project_readiness?: ProjectReadinessChecklist | null;
  vendor_questions?: VendorQuestions | null;
}

const VendorSearchResultsPage: React.FC = () => {
  const location = useLocation<LocationState>();
  const history = useHistory();
  // Removed isCallInProgress state - calls now happen in background without blocking UI
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [savedAnswers, setSavedAnswers] = useState<StoredQuoteAnswers | null>(null);
  const [calledVendors, setCalledVendors] = useState<string[]>([]);
  const [pendingVendorDetails, setPendingVendorDetails] = useState<{
    selectedVendors: string[];
    vendorDetails: VendorSearchResult[];
  } | null>(null);

  const updateSelectedVendorsMutation = useUpdateSelectedVendors();
  const initiateVendorCallsMutation = useInitiateVendorCalls();
  const searchVendorsMutation = useSearchVendors();
  const { selectedPropertyLocation } = usePropertyManager();
  const { user } = useAuth();

  const {
    vendors = [],
    searchQuery = '',
    address = '',
    disclaimer,
    search_query,
    searchId,
    selectedVendors: initialSelectedVendors = [],
    diy_video,
    cost_estimate,
    permit_requirement,
    project_readiness,
    vendor_questions,
  } = location.state || {};

  // Monitor distance calculation status if we have a searchId
  const { data: distanceStatus } = useDistanceCalculationStatus(searchId || '', !!searchId);

  useEffect(() => {
    // Clean up expired quote sessions on mount
    QuoteAnswerSessionService.clearExpiredSessions();

    // Restore vendor search state if it exists
    const storedState = sessionStorage.getItem('vendorSearchState');
    if (storedState && (!location.state || vendors.length === 0)) {
      try {
        const state = JSON.parse(storedState);
        // Ensure we have valid vendor data before proceeding
        if (state.vendors && Array.isArray(state.vendors) && state.vendors.length > 0) {
          history.replace(PMPRoutes.vendorSearchResults, state);
          sessionStorage.removeItem('vendorSearchState');
          return;
        }
      } catch (error) {
        // Invalid JSON in sessionStorage, clear it
        sessionStorage.removeItem('vendorSearchState');
      }
    }

    // If no vendors data and no valid stored state, redirect to search page
    if (!location.state || !vendors || vendors.length === 0) {
      const timer = setTimeout(() => {
        history.push(PMPRoutes.vendorSearch);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [location.state, vendors, history]);

  const handleNewSearch = () => {
    history.push(PMPRoutes.vendorSearch);
  };

  const handleEditSearch = async (newQuery: string) => {
    if (!address) {
      setSnackbarMessage('Address is required for search');
      return;
    }

    try {
      const result = await searchVendorsMutation.mutateAsync({
        address,
        query: newQuery,
      });

      if (result && result.vendors) {
        history.replace(location.pathname, {
          vendors: result.vendors,
          searchQuery: newQuery,
          address,
          disclaimer: result.disclaimer,
          search_query: result.search_query,
          searchId: result.searchId,
          diy_video: result.diy_video,
          cost_estimate: result.cost_estimate,
          permit_requirement: result.permit_requirement,
          project_readiness: result.project_readiness,
          vendor_questions: result.vendor_questions,
        });
      }
    } catch (error) {
      setSnackbarMessage('Failed to search providers. Please try again.');
    }
  };

  const handleCallVendors = async (
    selectedVendors: string[],
    vendorDetails: VendorSearchResult[]
  ) => {
    if (!selectedPropertyLocation) {
      setSnackbarMessage('Please select a property location first');
      throw new Error('No property location selected');
    }

    // Check if user has a phone number
    if (!user?.phone) {
      // Store vendor details for after phone number is added
      setPendingVendorDetails({ selectedVendors, vendorDetails });
      setShowPhoneModal(true);
      return;
    }

    // Store vendor details
    setPendingVendorDetails({ selectedVendors, vendorDetails });

    // Check if we have saved answers for this search
    const propertyAddress = address || selectedPropertyLocation.address_text;
    const storedAnswers = QuoteAnswerSessionService.getSavedAnswers(searchQuery, propertyAddress);

    if (storedAnswers) {
      // Show confirmation dialog to use saved answers
      setSavedAnswers(storedAnswers);
      setShowConfirmationDialog(true);
    } else {
      // No saved answers, show quote requirements dialog
      setShowQuoteDialog(true);
    }
  };

  const handleQuoteRequirementsSubmit = async (
    quoteAnswers: Record<number, string | number>,
    questions: QuoteQuestion[]
  ) => {
    if (!pendingVendorDetails || !selectedPropertyLocation) return;

    const { selectedVendors, vendorDetails } = pendingVendorDetails;

    setShowQuoteDialog(false);
    // Remove the loading state to keep the user on the search results page
    // setIsCallInProgress(true);

    try {
      // Track selected vendors in the database
      if (searchId) {
        try {
          await updateSelectedVendorsMutation.mutateAsync({
            searchId,
            selectedVendors,
          });
        } catch {
          // Silently handle the error - tracking is not critical for UX
        }
      }

      // Prepare call data for each vendor with quote requirements
      const vendorCallData = vendorDetails.map(vendor => ({
        phone_number: vendor.phone,
        vendor_name: vendor.name,
        search_query: searchQuery,
        location_id: selectedPropertyLocation.id,
        property_address: address || selectedPropertyLocation.address_text,
        property_email: selectedPropertyLocation.email?.value || '', // Add property email from nested object
        urgency: 'standard' as 'urgent' | 'standard', // Will be determined by AI analysis
        search_id: searchId, // Pass the search session ID
        requirements: JSON.stringify({
          questions_and_answers: questions.map(q => ({
            question: q.text,
            answer: quoteAnswers[q.id] || 'Not answered',
            question_type: q.input_type,
            required: q.required,
          })),
          raw_responses: quoteAnswers,
          emergency_service: vendor.emergency_service,
          service_type: searchQuery,
        }),
      }));

      // Initiate calls to all selected vendors
      const result = await initiateVendorCallsMutation.mutateAsync({
        vendors: vendorCallData,
      });

      if (result.success) {
        const successCount = result.summary.successful;
        const failCount = result.summary.failed;

        if (successCount > 0) {
          // Mark vendors as called only after successful submission
          setCalledVendors(prev => [...prev, ...selectedVendors]);
          
          const message =
            failCount > 0
              ? `✅ Terri is reaching out to ${successCount} provider${
                successCount > 1 ? 's' : ''
              } now! ${failCount} call${failCount > 1 ? 's' : ''} failed.`
              : `✅ Terri is reaching out to ${successCount} provider${
                successCount > 1 ? 's' : ''
              } now! You'll receive quotes shortly.`;
          setSnackbarMessage(message);
        } else {
          setSnackbarMessage('❌ Failed to initiate calls. Please try again.');
          throw new Error('Failed to initiate calls');
        }
      } else {
        setSnackbarMessage('❌ Failed to initiate provider calls. Please try again.');
        throw new Error('Failed to initiate vendor calls');
      }
    } catch (error) {
      // Show user-friendly error message with more context if available
      const errorMessage =
        error instanceof Error && error.message
          ? `❌ Call initiation failed: ${error.message}`
          : '❌ An error occurred while initiating calls. Please try again.';

      setSnackbarMessage(errorMessage);
      throw error;
    } finally {
      // setIsCallInProgress(false); // No longer needed since we're not setting it to true
    }
  };

  // Handle proceeding with saved answers
  const handleProceedWithSavedAnswers = () => {
    if (!savedAnswers) return;

    setShowConfirmationDialog(false);
    // Use the saved answers to initiate calls
    handleQuoteRequirementsSubmit(savedAnswers.answers, savedAnswers.questions);
  };

  // Handle editing saved answers
  const handleEditSavedAnswers = () => {
    setShowConfirmationDialog(false);
    // Show quote dialog with pre-filled answers
    setShowQuoteDialog(true);
  };

  // Handle phone number successfully saved
  const handlePhoneNumberSaved = () => {
    if (!pendingVendorDetails) {
      return;
    }

    // Continue with the vendor calling flow after phone number is saved
    // Check if we have saved answers for this search
    const propertyAddress = address || selectedPropertyLocation?.address_text;
    const storedAnswers = QuoteAnswerSessionService.getSavedAnswers(
      searchQuery,
      propertyAddress || ''
    );

    if (storedAnswers) {
      // Show confirmation dialog to use saved answers
      setSavedAnswers(storedAnswers);
      setShowConfirmationDialog(true);
    } else {
      // No saved answers, show quote requirements dialog
      setShowQuoteDialog(true);
    }
  };

  if (!location.state || !vendors) {
    return (
      <Box
        sx={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="body1" color="text.secondary">
            No results found. Redirecting to dashboard...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <>
      {/* AI Feature Banner */}
      <AIFeatureBanner />

      {/* Distance Calculation Status Banner */}
      {searchId && distanceStatus && !distanceStatus.calculation_complete && (
        <Paper
          sx={{
            p: 2,
            mb: 2,
            bgcolor: 'info.50',
            borderLeft: 4,
            borderColor: 'info.main',
          }}
          elevation={0}
        >
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'info.main' }}>
                Calculating Precise Distances
              </Typography>
              <Chip
                label={`${distanceStatus.calculated_distances}/${distanceStatus.total_vendors}`}
                size="small"
                color="info"
                variant="outlined"
              />
            </Box>

            <LinearProgress
              variant="determinate"
              value={(distanceStatus.calculated_distances / distanceStatus.total_vendors) * 100}
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: 'info.100',
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'info.main',
                },
              }}
            />

            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              We're calculating accurate driving distances. The provider list will automatically
              update with precise distances and improved ordering.
            </Typography>
          </Stack>
        </Paper>
      )}

      <VendorSearchResults
        vendors={vendors}
        searchQuery={searchQuery}
        address={address}
        disclaimer={disclaimer}
        search_query={search_query}
        searchId={searchId}
        isAuthenticated
        onNewSearch={handleNewSearch}
        onEditSearch={handleEditSearch}
        onCallVendors={handleCallVendors}
        isCallInProgress={searchVendorsMutation.isPending}
        showBackground={false}
        calledVendors={calledVendors}
        initialSelectedVendors={initialSelectedVendors}
        diyVideo={diy_video}
        costEstimate={cost_estimate}
        permitRequirement={permit_requirement}
        projectReadiness={project_readiness}
        vendorQuestions={vendor_questions}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={8000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      />

      {/* Quote Answers Confirmation Dialog */}
      {savedAnswers && pendingVendorDetails && (
        <QuoteAnswersConfirmationDialog
          open={showConfirmationDialog}
          onClose={() => {
            setShowConfirmationDialog(false);
            setSavedAnswers(null);
            setPendingVendorDetails(null);
          }}
          onProceedWithSavedAnswers={handleProceedWithSavedAnswers}
          onEditAnswers={handleEditSavedAnswers}
          savedAnswers={savedAnswers}
          vendorNames={pendingVendorDetails.vendorDetails.map(v => v.name)}
        />
      )}

      {/* Quote Requirements Dialog */}
      {pendingVendorDetails && (
        <QuoteRequirementsDialog
          open={showQuoteDialog}
          onClose={() => {
            setShowQuoteDialog(false);
            setPendingVendorDetails(null);
            setSavedAnswers(null);
          }}
          onSubmit={handleQuoteRequirementsSubmit}
          searchQuery={searchQuery}
          vendorNames={pendingVendorDetails.vendorDetails.map(v => v.name)}
          propertyAddress={address || selectedPropertyLocation?.address_text}
          initialAnswers={savedAnswers?.answers}
        />
      )}

      {/* Phone Number Modal */}
      <PhoneNumberModal
        open={showPhoneModal}
        onClose={() => {
          setShowPhoneModal(false);
          setPendingVendorDetails(null);
        }}
        onSuccess={handlePhoneNumberSaved}
        title="We just need your phone number so we can handle service calls for you."
        description="Don't worry — vendors will never see it. All calls and texts go through Terr.ai's number, so your info stays private. If a vendor has a quick follow-up, we'll text you."
      />
    </>
  );
};

export default VendorSearchResultsPage;
